import type { ClientNetManager } from '../networking/ClientNetManager';
import { Interpolator, rawDroneToInterpolated, type InterpolatedDrone } from '../networking/Interpolator';
import { InputSender } from '../networking/InputSender';
import type { SceneManager } from '../rendering/SceneManager';
import { DroneRenderer } from '../rendering/DroneRenderer';
import { ArenaRenderer } from '../rendering/ArenaRenderer';
import { PickupRenderer } from '../rendering/PickupRenderer';
import { EffectsManager } from '../rendering/EffectsManager';
import { AttackVisualizer } from '../rendering/AttackVisualizer';
import { FocusFireReticle } from '../rendering/FocusFireReticle';
import { CageSurroundIndicator } from '../rendering/CageSurroundIndicator';
import { CameraController } from '../rendering/CameraController';
import { StatusEffectOverlay } from '../rendering/StatusEffectOverlay';
import { SpectateCamera, type PlayerId } from '../rendering/SpectateCamera';
import { ZoneRenderer } from '../rendering/ZoneRenderer';
import type { HudManager } from '../ui/HudManager';
import type { InputManager } from '../input/InputManager';
import { PointerLockController } from '../input/PointerLockController';
import { TouchHandler } from '../input/TouchHandler';
import type { AudioManager } from '../audio/AudioManager';
import { isAdminMode } from '../util/url';
import {
  DEFAULT_VISUAL_QUALITY_PRESET,
  type VisualQualityPreset,
} from '../rendering/visualConfig';
import {
  DEFAULT_WORLD_THEME,
  type WorldThemeDefinition,
} from '../rendering/worldTheme';
import { ServerEvent, GameMode, GamePhase, ALGORITHM_CARDS, AlgorithmCardType, Tier, type KillStreakLevel } from '../../shared/types';
import { playCardActivationByType } from './cardActivationAudio';
import {
  CLIENT_INTERPOLATION_BUFFER_MS,
  RESPAWN_DELAY_MS,
  COLYSEUS_PORT,
  TRAINING_DUMMY_ID,
  REMATCH_MIN_VOTES,
  REMATCH_VOTE_WINDOW_SEC,
} from '../../shared/constants';
import { type Vec3 } from '../../shared/utils';
import { DroneStateManager } from './DroneStateManager';
import { AlgorithmCardManager } from './AlgorithmCardManager';
import { PlayerStateTracker } from './PlayerStateTracker';
import { wireUiCommands } from './wireUiCommands';
import {
  shouldIgnoreDamageEvent,
  isLocalPlayerInvolved,
  isSelfHeal,
  routeCardEffectFailed,
  type LocalGateState,
} from './eventGating';
import { dispatchDamageImpact } from './damageImpactDispatcher';
import { HitMarkers } from '../ui/HitMarkers';
import { DamageFloaters } from '../ui/DamageFloaters';
import { DamageDirection } from '../ui/DamageDirection';
import { MouseSettingsPanel } from '../ui/MouseSettingsPanel';
import type { CombatLog } from '../ui/CombatLog';
import { CritAttackerFeedback } from '../effects/CritAttackerFeedback';
import { uRimStrength as tslURimStrength } from '../rendering/Shaders';
import { HitstopController } from '../effects/HitstopController';
import { triggerHeavyHitShake } from '../rendering/postProcessingState';

const STREAK_ANNOUNCEMENTS: Record<KillStreakLevel, { label: string; soundLevel: 1 | 2 | 3 | 4 }> = {
  DOUBLE: { label: 'DOUBLE KILL!', soundLevel: 1 },
  TRIPLE: { label: 'TRIPLE KILL!', soundLevel: 2 },
  QUAD:   { label: 'QUAD KILL!',   soundLevel: 3 },
  PENTA:  { label: 'PENTAKILL!',   soundLevel: 4 },
};

export interface GameClientDeps {
  scene: SceneManager;
  hud: HudManager;
  input: InputManager;
  net: ClientNetManager;
  audio?: AudioManager;
  qualityPreset?: VisualQualityPreset;
  worldTheme?: WorldThemeDefinition;
}

export class GameClient {
  private scene: SceneManager;
  private hud: HudManager;
  private input: InputManager;
  private net: ClientNetManager;
  private audio: AudioManager | null;
  private qualityPreset: VisualQualityPreset;
  private worldTheme: WorldThemeDefinition;

  private interpolator: Interpolator | null = null;
  private inputSender: InputSender | null = null;
  private droneRenderer: DroneRenderer | null = null;
  private arenaRenderer: ArenaRenderer | null = null;
  private pickupRenderer: PickupRenderer | null = null;
  private effectsManager: EffectsManager | null = null;
  private attackVisualizer: AttackVisualizer | null = null;
  private focusFireReticle: FocusFireReticle | null = null;
  private cageSurroundIndicator: CageSurroundIndicator | null = null;
  private cameraController: CameraController | null = null;
  private pointerLockController: PointerLockController | null = null;
  private touchHandler: TouchHandler | null = null;
  private zoneRenderer: ZoneRenderer | null = null;
  private statusOverlay: StatusEffectOverlay | null = null;
  private hitMarkers: HitMarkers | null = null;
  private damageFloaters: DamageFloaters | null = null;
  private damageDirection: DamageDirection | null = null;
  private mouseSettings: MouseSettingsPanel | null = null;
  private spectateCamera: SpectateCamera | null = null;
  private spectateKeyHandler: ((e: KeyboardEvent) => void) | null = null;
  private static readonly KILL_CAM_DURATION_MS = 2000;

  private droneState = new DroneStateManager();
  private cardManager = new AlgorithmCardManager();
  private playerTracker = new PlayerStateTracker();

  private started = false;
  private prevDroneCount = 0;
  private prevCardCount = 0;
  private brPanelVisible = false;
  private matchEndShown = false;
  private lowHpAudioActive = false;
  private vhsOffTimer: ReturnType<typeof setTimeout> | null = null;
  private static readonly VHS_LEGENDARY_DURATION_MS = 1200;
  private combatLog: CombatLog | null = null;
  private combatLogKeyHandler: ((e: KeyboardEvent) => void) | null = null;
  private pointerLockChangeHandler: (() => void) | null = null;
  // Per-slot rising-edge tracker for card cooldownRemaining. `null` means
  // we have not yet seen this slot — pretend its cooldown was 0 so the
  // initial spawn (cards arriving with cooldown 0) does NOT chirp.
  private prevCardCooldowns: Array<number | null> = [];
  private critAttackerFeedback: CritAttackerFeedback | null = null;
  private hitstopController = new HitstopController();

  constructor(deps: GameClientDeps) {
    this.scene = deps.scene;
    this.hud = deps.hud;
    this.input = deps.input;
    this.net = deps.net;
    this.audio = deps.audio ?? null;
    this.qualityPreset = deps.qualityPreset ?? DEFAULT_VISUAL_QUALITY_PRESET;
    this.worldTheme = deps.worldTheme ?? DEFAULT_WORLD_THEME;
  }

  async start(username: string, mode: GameMode = GameMode.Free, loadout?: number[]): Promise<void> {
    const admin = isAdminMode();
    const enemyParam = new URLSearchParams(window.location.search).get('enemy');

    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    // Vite injects `import.meta.env.VITE_*` at build time; we read without
    // depending on Vite's client type augmentation.
    const env = (import.meta as unknown as { env?: Record<string, string | undefined> }).env ?? {};
    const explicitUrl = env.VITE_GAME_SERVER_URL;
    const portOverride = env.VITE_COLYSEUS_PORT;
    const port = portOverride ? Number(portOverride) : COLYSEUS_PORT;
    const serverUrl = explicitUrl || `${wsProtocol}//${window.location.hostname}:${port}`;

    const joinOptions: { admin?: boolean; mode?: GameMode; loadout?: number[]; enemy?: string } = {};
    if (admin) joinOptions.admin = true;
    if (mode === GameMode.Br) joinOptions.mode = GameMode.Br;
    if (loadout) joinOptions.loadout = loadout;
    // Forward `?enemy=...` to the server only in admin mode; the server
    // ignores it otherwise.
    if (admin && enemyParam) joinOptions.enemy = enemyParam;

    this.interpolator = new Interpolator(CLIENT_INTERPOLATION_BUFFER_MS);
    this.wireStateSync();

    try {
      await this.net.join(serverUrl, username, joinOptions);
    } catch (e) {
      console.error('Failed to connect:', e);
      return;
    }

    const scene3d = this.scene.getScene();

    this.arenaRenderer = new ArenaRenderer(
      scene3d,
      this.scene.getSunPosition(),
      this.scene.getCamera(),
      this.qualityPreset,
      this.worldTheme,
    );
    this.droneRenderer = new DroneRenderer(scene3d, this.qualityPreset, this.worldTheme);
    this.droneRenderer.setSunDirection(this.scene.getSunPosition());
    this.pickupRenderer = new PickupRenderer(scene3d);
    this.effectsManager = new EffectsManager(scene3d, this.qualityPreset);
    this.attackVisualizer = new AttackVisualizer(scene3d, this.qualityPreset);
    this.focusFireReticle = new FocusFireReticle(scene3d);
    this.cageSurroundIndicator = new CageSurroundIndicator(scene3d);
    this.zoneRenderer = new ZoneRenderer(scene3d);
    this.statusOverlay = new StatusEffectOverlay(scene3d);
    this.statusOverlay.setCamera(this.scene.getCamera());
    this.cameraController = new CameraController(
      this.scene.getCamera(),
      this.scene.getRenderer().domElement,
      this.qualityPreset,
    );
    // Mouse-look tuning panel (toggled with `O`). Apply persisted settings up
    // front so the very first applyMouseDelta call honors the user's choice,
    // then subscribe so each slider tweak flows back to the camera.
    this.mouseSettings = new MouseSettingsPanel(this.hud.getRoot());
    this.mouseSettings.onChange((s) => {
      this.cameraController?.setMouseSensitivity(s.sensitivity, s.invertPitch);
    });
    this.cameraController.setMouseSensitivity(
      this.mouseSettings.getSensitivity(),
      this.mouseSettings.getInvertPitch(),
    );
    this.spectateCamera = new SpectateCamera(
      {
        getPlayerLeadPosition: (id) => this.lookupPlayerLeadPosition(id),
        getPlayerAnyPosition: (id) => this.lookupPlayerAnyPosition(id),
        getPlayerName: (id) => this.lookupPlayerName(id),
      },
      {
        onCaption: (text) => this.hud.setKilledByCaption(text),
        onKillCamEnd: () => this.handleKillCamEnd(),
      },
    );
    this.cameraController.attachSpectateCamera(this.spectateCamera);
    this.hud.onSpectateClick(() => this.beginSpectate());
    this.spectateKeyHandler = (e) => this.handleSpectateKey(e);
    window.addEventListener('keydown', this.spectateKeyHandler);
    this.inputSender = new InputSender(this.net);

    this.input.onCameraOrbit((dx, dy) => this.cameraController!.applyMouseDelta(dx, dy));
    this.input.onCameraZoom((d) => this.cameraController!.applyZoom(d));

    // Mobile/iPad touch input — drives the same camera consumers as the mouse.
    // One finger swipes rotate the camera, two-finger pinch zooms.
    this.touchHandler = new TouchHandler(this.scene.getRenderer().domElement);
    this.touchHandler.onDrag((dx, dy) => this.cameraController!.applyMouseDelta(dx, dy));
    this.touchHandler.onScroll((d) => this.cameraController!.applyZoom(d));
    this.touchHandler.attach();
    this.input.onCommand((cmd) => this.inputSender!.queueCommand(cmd));

    // FPS pointer-lock. Coexists with MouseHandler's right-drag orbit — the
    // ownership flag in MouseHandler keeps the two controllers from both
    // processing the same locked-mousemove and double-rotating.
    this.pointerLockController = new PointerLockController(this.scene.getRenderer().domElement);
    this.pointerLockController.onDrag((dx, dy) => this.cameraController!.applyMouseDelta(dx, dy));
    this.pointerLockController.attach();
    this.input.setDroneGroup(this.droneRenderer.getDroneGroup());
    // Mirror the InputManager's throttled hover state into the DroneRenderer
    // so the rim-glow highlight tracks the cursor.
    this.input.onDroneHover((droneId) => {
      this.droneRenderer?.setHoveredDrone(droneId);
    });

    this.wireServerEvents();
    wireUiCommands(this.hud, this.inputSender, () => this.getCameraForwardDirection());
    this.input.onAbilityTarget((slot) => this.resolveAbilityTargetDroneId(slot));

    this.hitMarkers = new HitMarkers(
      this.hud.getHitMarkersLayer(),
      () => this.getLocalGateState(),
    );
    this.damageFloaters = new DamageFloaters(
      this.hud.getDamageFloatersLayer(),
      this.scene.getCamera(),
      () => this.getLocalGateState(),
      (attackerId) => this.lookupDroneTypeById(attackerId),
    );
    this.damageDirection = new DamageDirection(
      this.hud.getDamageDirectionLayer(),
      this.scene.getCamera(),
      () => ({
        ...this.getLocalGateState(),
        swarmHpFraction: this.computeSwarmHpFraction(),
      }),
    );
    this.hitMarkers.attach(this.net);
    this.damageFloaters.attach(this.net);
    this.damageDirection.attach(this.net);

    // Lives in the damage-floaters layer (sharing the HUD slot) but inside
    // its own child container so the cleanup pools never collide. The
    // applyRimMultiplier callback scales the shared `uRimStrength` uniform
    // around its baseline (captured at construction time so future Shader
    // tuning carries through). m === 1.0 restores the baseline.
    const rimUniform = tslURimStrength as unknown as { value: number };
    const rimBaseline = rimUniform.value;
    this.critAttackerFeedback = new CritAttackerFeedback({
      layer: this.hud.getDamageFloatersLayer(),
      camera: this.scene.getCamera(),
      getGateState: () => this.getLocalGateState(),
      applyRimMultiplier: (m) => { rimUniform.value = rimBaseline * m; },
      pulseAttackerDrone: (id) => this.droneRenderer?.pulseCritGlow(id),
    });

    this.combatLog = this.hud.initCombatLog({
      now: () => Date.now(),
      getSelectedDroneId: () => this.resolveAbilityTargetDroneId() ?? '',
      resolvePlayerName: (id) => this.lookupPlayerName(id),
    });
    this.combatLogKeyHandler = (e) => this.handleCombatLogKey(e);
    window.addEventListener('keydown', this.combatLogKeyHandler);

    // FPS-mode crosshair: visible only while the pointer is locked. Native
    // `pointerlockchange` fires for any element acquiring/releasing lock, so
    // it covers BOTH PointerLockController (FPS click-to-lock) and MouseHandler
    // (legacy right-drag), without needing to subscribe to each separately.
    if (typeof document !== 'undefined') {
      this.pointerLockChangeHandler = () => {
        this.hud.getCrosshair().setVisible(document.pointerLockElement !== null);
      };
      document.addEventListener('pointerlockchange', this.pointerLockChangeHandler);
    }

    this.input.setEnabled(true);
    if (typeof window !== 'undefined' && (import.meta as any).env?.DEV) {
      (window as any).__game = this;
    }
    // Pick the correct initial overlay up-front to avoid a one-frame flash of
    // the game HUD before the first state patch arrives for BR lobby joiners.
    const initialState = this.net.getRoom()?.state as any;
    if (initialState?.mode === GameMode.Br && initialState?.phase === GamePhase.Waiting) {
      this.hud.showLobby(() => this.handleLeaveLobby());
      this.hud.getLobbyScreen().update(initialState, this.net.getSessionId());
    } else {
      this.hud.showGameHud();
    }
    this.started = true;
    const currentState = this.net.getRoom()?.state as any;
    if (currentState) this.handleStateSnapshot(currentState);
    this.scheduleStateHydrationReplay();
    this.audio?.playAmbient();
    this.audio?.playBgmLoop();
    this.wireUiClickAudio();
    this.wireAudioUnlock();
  }

  /**
   * Capture-phase click delegation so any HUD button plays a tick even when
   * the button stops propagation. Hover SFX is intentionally omitted —
   * pointer events fire per descendant transition and would chatter.
   */
  private wireUiClickAudio(): void {
    if (!this.audio || typeof document === 'undefined') return;
    document.addEventListener(
      'click',
      (e) => {
        const t = e.target;
        if (!(t instanceof Element)) return;
        if (!t.closest('button, [role="button"], .card-slot, .drone-status-row, .controls-toggle')) return;
        this.audio?.playUiClick(false);
      },
      true,
    );
  }

  /**
   * Resume the AudioContext on the first user gesture. Capture phase so
   * in-game handlers that `preventDefault()` or pointer-lock acquisition
   * don't swallow the unlock event before we see it.
   */
  private wireAudioUnlock(): void {
    if (!this.audio || typeof window === 'undefined') return;
    const events: Array<'mousedown' | 'touchstart' | 'keydown'> = ['mousedown', 'touchstart', 'keydown'];
    const handler = () => {
      this.audio?.resumeAudioContext();
      for (const ev of events) {
        window.removeEventListener(ev, handler, true);
      }
    };
    for (const ev of events) {
      window.addEventListener(ev, handler, true);
    }
  }

  private handleLeaveLobby(): void {
    try {
      this.net.disconnect();
    } catch (e) {
      console.error('Failed to disconnect from room:', e);
    }
    location.reload();
  }

  update(rawDeltaMs: number): void {
    if (!this.started) return;

    const now = performance.now();
    const drones = this.getFrameDrones(now);
    const roomState = this.net.getRoom()?.state as any;
    const localId = roomState ? this.resolveLocalSessionId(roomState) : this.net.getSessionId();
    this.droneState.updateDrones(drones, localId);
    this.cameraController!.setFollowTarget(this.droneState.getSwarmCenter());
    // Camera always sees raw wall-clock dt so its hit-stop timer + shake
    // decay run in real time. Other renderers see the scaled `deltaMs`
    // below, which produces the slow-mo visual.
    this.cameraController!.update(rawDeltaMs);
    // Outside the hit-stop window getTimeScale() returns 1.0, so this is a
    // no-op pass-through; during the slow window every downstream renderer
    // receives a smaller deltaMs and animates in slow motion.
    // === U11: layer the local-player-kill hit-stop on top of U4's crit
    // hit-stop. Both helpers return 1.0 outside their windows, so when no
    // event is in flight the product is 1.0 (no slowdown). The HitstopController
    // is ticked AFTER reading its scale so the active frame still feels the
    // full slowdown — the same pattern CameraController uses for its window.
    const deltaMs =
      rawDeltaMs *
      this.cameraController!.getTimeScale() *
      this.hitstopController.currentTimeScale();
    this.hitstopController.update(rawDeltaMs);

    // Camera angle MUST be fed into InputManager BEFORE input.update() reads
    // it via getMoveDirection(), otherwise WASD direction lags one frame
    // behind the camera (~16ms). cameraController.update() above produces
    // the latest orbit angle; we forward that, then tick input.
    this.input.setCameraOrbitAngle(this.cameraController!.getOrbitAngle());
    this.input.update(deltaMs);
    this.inputSender!.flush();

    this.droneRenderer!.setCameraPosition(this.scene.getCamera().position);
    this.droneRenderer!.setSunDirection(this.scene.getSunPosition());
    this.droneRenderer!.update(drones, localId, deltaMs);
    this.statusOverlay?.update(drones, deltaMs);
    this.pickupRenderer!.setCameraPosition(this.scene.getCamera().position);
    this.updatePickups(deltaMs);

    this.effectsManager!.update(deltaMs);
    this.attackVisualizer!.update(deltaMs);
    this.updateCardTargetVisuals(deltaMs, drones, roomState, localId);
    this.arenaRenderer!.update(deltaMs);
    this.zoneRenderer!.update(deltaMs, this.net.getRoom()?.state);

    this.hitMarkers?.update(deltaMs);
    this.damageFloaters?.update(deltaMs);
    this.damageDirection?.update(deltaMs);
    this.combatLog?.update(deltaMs);
    // Tick rim-pulse envelopes; the helper writes the uniform itself.
    this.critAttackerFeedback?.update(deltaMs);

    // Sum local player's swarm HP / maxHP each frame. `getOwnDrones()` only
    // returns alive drones, so all-dead → 0 (peak escalation).
    const own = this.droneState.getOwnDrones();
    let totalHp = 0;
    let totalMaxHp = 0;
    for (const d of own) {
      totalHp += d.health;
      totalMaxHp += d.maxHealth;
    }
    this.scene.setLowHpFraction(totalMaxHp > 0 ? totalHp / totalMaxHp : 0);

    this.updateHud(drones, localId);
  }

  /**
   * Resolve the local player's `focusTargetId` / `cageTargetId` against
   * this frame's interpolated drones, then drive the reticle and cage
   * indicator. Both renderers hide when the corresponding id is empty
   * or the resolved drone is missing/dead — preventing stale visuals.
   */
  private updateCardTargetVisuals(
    deltaMs: number,
    drones: InterpolatedDrone[],
    roomState: any,
    localId: string,
  ): void {
    if (!this.focusFireReticle || !this.cageSurroundIndicator) return;

    const camera = this.scene.getCamera();
    const localPlayer = roomState?.players?.get?.(localId);
    const focusId: string = localPlayer?.focusTargetId ?? '';
    const cageId: string = localPlayer?.cageTargetId ?? '';

    const focusDrone = focusId ? drones.find((d) => d.id === focusId && d.alive) : null;
    const cageDrone = cageId ? drones.find((d) => d.id === cageId && d.alive) : null;

    this.focusFireReticle.update(
      deltaMs,
      focusDrone ? { position: focusDrone.position } : null,
      camera,
    );
    this.cageSurroundIndicator.update(
      deltaMs,
      cageDrone ? { position: cageDrone.position } : null,
    );
  }

  /**
   * Aggregate fraction of local-player swarm HP. Drives the low-HP red
   * pulse in {@link DamageDirection.update}. 0 if the player has no living
   * drones (treated as "not on screen" — no pulse).
   */
  private computeSwarmHpFraction(): number {
    const own = this.droneState.getOwnDrones();
    if (own.length === 0) return 0;
    let cur = 0;
    let max = 0;
    for (const d of own) {
      cur += Math.max(0, d.health);
      max += Math.max(0, d.maxHealth);
    }
    return max > 0 ? cur / max : 0;
  }

  private wireStateSync(): void {
    this.net.onStateChange((state) => this.handleStateSnapshot(state));
  }

  private scheduleStateHydrationReplay(): void {
    for (const delayMs of [0, 100, 500, 1000, 2000, 4000]) {
      setTimeout(() => {
        const state = this.net.getRoom()?.state as any;
        if (state) this.handleStateSnapshot(state);
      }, delayMs);
    }
  }

  private handleStateSnapshot(state: any): void {
    this.interpolator!.pushSnapshot(state, performance.now());
    const localSessionId = this.resolveLocalSessionId(state);
    this.playerTracker.syncFromState(state, localSessionId);

    // Runs before the localPlayer guard below so the lobby paints even on
    // snapshots where our player row hasn't yet materialized.
    this.syncLobbyVisibility(state);

    const localPlayer = state.players?.get?.(localSessionId);
    if (!localPlayer) return;

    this.cardManager.syncFromServer(localPlayer.cards);

    this.detectPickups(localPlayer);

    // In BR mode eliminated players don't respawn — they see the
    // dedicated eliminated overlay (fired on PlayerEliminated event)
    // instead of the respawn countdown.
    const isEliminated = !!(localPlayer as any).eliminated;
    if (!localPlayer.alive && this.started && !isEliminated) {
      this.hud.showRespawnOverlay(RESPAWN_DELAY_MS);
    } else if (localPlayer.alive) {
      this.hud.hideRespawnOverlay();
    }

    if (state.phase === GamePhase.Finished && this.started && !this.matchEndShown) {
      const entries = this.buildLeaderboardEntries(state);
      this.hud.showMatchEndOverlay(entries, localPlayer.username ?? '');
      this.wireRematchOverlay();
      this.matchEndShown = true;
      this.playMatchEndAudio(entries[0]?.username, localPlayer.username ?? '');
    }

    if (state.mode === GameMode.Br && state.phase === GamePhase.Playing) {
      this.hud.getBrStatusPanel().update(state);
    }
  }

  private resolveAbilityTargetDroneId(slot?: number): string | null {
    const state = this.net.getRoom()?.state as any;
    if (!state) return null;
    const player = state.players?.get?.(this.resolveLocalSessionId(state));
    if (!player) return null;
    if (slot !== undefined) {
      const drone = player.drones?.[slot];
      return drone?.alive ? drone.id : null;
    }
    const selected = player.selectedDroneIds?.[0] || player.selectedDroneId || player.leaderDroneId;
    if (selected) return selected;
    for (const d of player.drones ?? []) {
      if (d.alive) return d.id;
    }
    return null;
  }

  private getCameraForwardDirection(): Vec3 {
    const angle = this.cameraController?.getOrbitAngle() ?? 0;
    return {
      x: -Math.cos(angle),
      y: 0,
      z: -Math.sin(angle),
    };
  }

  private resolveLocalSessionId(state: any): string {
    const sessionId = this.net.getSessionId();
    if (sessionId && state.players?.get?.(sessionId)) return sessionId;

    let fallback = '';
    state.players?.forEach?.((player: any) => {
      if (!fallback && player.sessionId !== TRAINING_DUMMY_ID) {
        fallback = player.sessionId;
      }
    });
    return fallback || sessionId;
  }

  private syncLobbyVisibility(state: any): void {
    const lobby = this.hud.getLobbyScreen();
    const inLobby = state?.mode === GameMode.Br && state?.phase === GamePhase.Waiting;

    if (inLobby) {
      if (!lobby.isVisible()) {
        this.hud.showLobby(() => this.handleLeaveLobby());
      }
      lobby.update(state, this.net.getSessionId());
    } else if (lobby.isVisible()) {
      this.hud.hideLobby();
      this.hud.showGameHud();
    }
  }

  private buildLeaderboardEntries(state: any): Array<{ username: string; score: number; kills: number }> {
    const entries: Array<{ username: string; score: number; kills: number }> = [];
    state?.players?.forEach?.((p: any) => {
      if (p.sessionId === TRAINING_DUMMY_ID) return;
      entries.push({ username: p.username ?? 'anon', score: p.score ?? 0, kills: p.kills ?? 0 });
    });
    entries.sort((a, b) => b.score - a.score || b.kills - a.kills);
    return entries;
  }

  private wireServerEvents(): void {
    this.net.onServerEvent((eventType: string, data: Record<string, unknown>) => {
      // Forward to the diagnostic panel before the main switch so the log
      // captures every relevant event regardless of the visual handlers
      // returning early (e.g., shouldIgnoreDamageEvent guards).
      this.combatLog?.handleEvent(eventType, data);
      switch (eventType) {
        case ServerEvent.DroneDestroyed:
          if (data?.position) {
            this.effectsManager!.spawnExplosion(data.position as Vec3);
            const localExp = isLocalPlayerInvolved(data, this.getLocalGateState());
            this.audio?.playExplosion();
            if (localExp) {
              this.cameraController?.addShake(0.18);
              this.cameraController?.addFovKick(0.8);
              this.scene.triggerCardPost(0.14);
            } else {
              this.cameraController?.addShake(0.04);
            }
          }
          this.maybeTriggerKillHitstop(data);
          this.maybeTriggerKillCamOnDroneDeath(data);
          break;
        case ServerEvent.ProjectileFired:
          if (data?.origin && data?.end) {
            this.attackVisualizer!.showAttack(data.origin as Vec3, data.end as Vec3, 0);
          }
          if (data?.playerId === this.getLocalGateState().localSessionId) {
            this.audio?.playAttackChirp();
          }
          break;
        case ServerEvent.DamageDealt:
          if (shouldIgnoreDamageEvent(data)) break;
          dispatchDamageImpact(eventType, data, {
            attackerVisualizer: this.attackVisualizer!,
            effects: this.effectsManager!,
            audio: this.audio,
            camera: this.cameraController,
            scene: this.scene,
            dispatchImpactAudio: (id) => this.dispatchImpactAudio(id),
            triggerHeavyHitShake: (d, g) => this.maybeTriggerHeavyHitShake(d, g),
            critAttackerFeedback: this.critAttackerFeedback,
            getGateState: () => this.getLocalGateState(),
          });
          break;
        case ServerEvent.ShieldBroken:
          // Support's Shield Pulse absorbed a hit. White expanding-ring
          // flash + descending-triad chime + a gentle post-FX bump (this is
          // a "good" event for the defender, NOT triggerDamagePost).
          if (data?.position) {
            this.effectsManager!.showShieldBreak(data.position as Vec3);
            // Layer a chromatic shimmer on top of the cyan ring so back-to-
            // back saves still read as discrete events.
            this.effectsManager!.showShieldBreakShimmer(data.position as Vec3);
          }
          this.audio?.playShieldBreak();
          this.scene.triggerCardPost(0.10);
          // Layer a high-pitch metallic shatter sweep over the streak triad
          // so an absorbed hit reads instantly different from a normal damage
          // tick — distinct timbre (band-passed square) cuts through.
          this.audio?.playShieldBreakShatter();
          break;
        case ServerEvent.HealApplied:
          if (data?.position && data?.radius) {
            const pos = data.position as Vec3;
            const radius = data.radius as number;
            if (isSelfHeal(data)) {
              this.effectsManager!.showSelfHealAura(pos, radius);
            } else {
              this.effectsManager!.showHealAura(pos, radius);
            }
            this.audio?.playHeal();
            this.scene.triggerCardPost(0.08);
          }
          break;
        case ServerEvent.PlayerKilled:
          if (data?.killerName && data?.playerName) {
            this.hud.showKillNotification(
              data.killerName as string,
              data.playerName as string,
            );
          }
          break;
        case ServerEvent.KillStreak: {
          // Self-only display: the announcer is a personal "you're on fire"
          // moment, not a hostile killcam. Echoing every player's streak
          // would spam the screen during chaotic teamfights.
          if (data?.playerId !== this.net.getSessionId()) break;
          const announcement = STREAK_ANNOUNCEMENTS[data?.level as KillStreakLevel];
          if (!announcement) break;
          this.hud.showToast(announcement.label, 'amber');
          // Richer bottom-center toast (scale-bounce + golden gradient at 4+).
          // Numeric kill count is preferred when present; otherwise derive
          // from the symbolic level so the toast still triggers.
          {
            const numeric = typeof data?.killCount === 'number'
              ? (data.killCount as number)
              : ({ DOUBLE: 2, TRIPLE: 3, QUAD: 4, PENTA: 5 } as Record<string, number>)[
                  data?.level as string
                ] ?? 0;
            this.hud.getKillStreakToast().show(numeric);
          }
          this.audio?.playStreakSound(announcement.soundLevel);
          this.cameraController?.addFovKick(0.3);
          // Note-count overlay on the streak diad — QUAD/PENTA share the
          // spree (4-note + sub-rumble) variant, so map streak 1..4 → 1..3.
          this.audio?.playKillStreak(Math.min(announcement.soundLevel, 3));
          break;
        }
        case ServerEvent.PickupCollected:
          if (data?.position) {
            // Sparkle reads as "you grabbed something" (type-tinted vertical
            // fountain); the burst preserves the pre-existing card-style pop.
            this.effectsManager!.showPickupSparkle(data.position as Vec3, data.pickupType as number);
            this.effectsManager!.showCardActivation(data.position as Vec3, data.pickupType as number);
          }
          this.audio?.playPickup();
          this.scene.triggerCardPost(0.18);
          break;
        case ServerEvent.CardActivated:
          if (data?.position) {
            // Per-card signature (Guardian aura, Sniper laser, Healer ring, …)
            // fired BEFORE the generic formation burst so the signature reads
            // as the dominant cue. Unknown card types fall back to the burst.
            // The resolver re-reads the caster's centroid each tick so VFX
            // follow a moving caster instead of staying pinned to spawn.
            const casterPlayerId = data.playerId as string | undefined;
            const liveResolver = casterPlayerId
              ? () => this.computeLivePlayerCentroid(casterPlayerId)
              : undefined;
            this.effectsManager!.spawnCardSignature(data.cardType as number, data.position as Vec3, {
              liveResolver,
            });
            this.effectsManager!.showCardActivation(data.position as Vec3, data.cardType as number);
            // Server-computed centroid is already the active-drone average
            // (see GameRoom.onMessage activate_card). Fire the formation
            // burst alongside the existing card-activation pop.
            this.effectsManager!.showFormationBurst(data.position as Vec3, data.cardType as number);
          }
          playCardActivationByType(this.audio, data?.cardType as number | undefined);
          this.cameraController?.addFovKick(0.4);
          this.scene.triggerCardPost(0.22);
          // Legend-tier card by the local player triggers a brief VHS post-FX
          // pulse for cinematic flair. Common/Rare cards do nothing here.
          this.maybeTriggerLegendVHS(data);
          break;
        case ServerEvent.AbilityTriggered:
          if (data?.position) {
            this.effectsManager!.showCardActivation(data.position as Vec3, data.droneType as number);
          }
          this.audio?.playCardActivate();
          this.cameraController?.addFovKick(0.3);
          this.scene.triggerCardPost(0.15);
          break;
        case ServerEvent.PlayerRespawned:
          if (data?.playerId === this.net.getSessionId()) {
            this.hud.hideRespawnOverlay();
          }
          break;
        case ServerEvent.PlayerEliminated: {
          const username = (data?.username as string) ?? 'anon';
          const placement = (data?.placement as number) ?? 0;
          const eliminatedBy = data?.eliminatedBy as string | undefined;
          this.hud.showEliminationNotification({ username, placement, eliminatedBy });
          if (data?.playerId === this.net.getSessionId()) {
            const total = this.getLivingPlayerTotal();
            this.hud.showEliminatedOverlay(placement, total);
            // BR-specific: kill-cam → wait for it to expire → onKillCamEnd
            // transitions to spectate. If no killer is known we skip the
            // freeze-frame and allow the user to click SPECTATE manually.
            if (eliminatedBy && this.spectateCamera) {
              this.spectateCamera.triggerKillCam(eliminatedBy, GameClient.KILL_CAM_DURATION_MS);
            }
            // Local-player elimination plays VHS for the kill-cam window so
            // the freeze-frame reads as a dramatic "you died" beat.
            this.pulseVHS(GameClient.VHS_LEGENDARY_DURATION_MS);
          }
          break;
        }
        case ServerEvent.ZoneShrinking: {
          const stageIndex = (data?.stageIndex as number) ?? 0;
          this.hud.showToast(`ZONE CLOSING — STAGE ${stageIndex + 1}`, 'amber');
          this.cameraController?.addShake(0.12);
          break;
        }
        case ServerEvent.MatchStarting: {
          const count = (data?.playerCount as number) ?? 0;
          this.hud.showToast(`MATCH STARTED — ${count} CONTESTANTS`, 'cyan');
          break;
        }
        case ServerEvent.MatchWon: {
          const roomState = this.net.getRoom()?.state as any;
          const localPlayer = roomState?.players?.get?.(this.net.getSessionId());
          const entries = this.buildLeaderboardEntries(roomState);
          // Server authority: if MatchWon names a winner, put them on top
          // regardless of score (score tiebreak could disagree).
          const winnerUsername = (data?.winnerUsername as string | null) ?? null;
          if (winnerUsername) {
            const idx = entries.findIndex((e) => e.username === winnerUsername);
            if (idx > 0) {
              const [w] = entries.splice(idx, 1);
              entries.unshift(w);
            }
          }
          // Free-mode payload carries a stats array; forward it so the
          // rich post-match table renders in place of the basic leaderboard.
          const richStats = Array.isArray(data?.stats)
            ? (data!.stats as Array<Record<string, unknown>>).map((s) => ({
                sessionId: String(s.sessionId ?? ''),
                username: String(s.username ?? ''),
                placement: Number(s.placement ?? 0),
                score: Number(s.score ?? 0),
                kills: Number(s.kills ?? 0),
                damageDealt: Number(s.damageDealt ?? 0),
                damageTaken: Number(s.damageTaken ?? 0),
                longestStreak: Number(s.longestStreak ?? 0),
                timeAliveMs: Number(s.timeAliveMs ?? 0),
              }))
            : undefined;
          this.hud.showMatchEndOverlay(entries, localPlayer?.username ?? '', richStats);
          this.playMatchEndAudio(winnerUsername ?? undefined, localPlayer?.username ?? '');
          this.wireRematchOverlay();
          break;
        }
        case ServerEvent.DroneConverted: {
          const pos = data?.position as Vec3 | undefined;
          if (pos) {
            this.effectsManager!.spawnGlitchAt(pos);
          }
          this.scene.setVHSEnabled(true);
          setTimeout(() => this.scene.setVHSEnabled(false), 240);
          this.audio?.playCardActivate();
          this.cameraController?.addShake(0.10);

          // Optional kill-feed entry: "<attacker> converted <victim>'s drone".
          // Both usernames are resolved via the existing lookupPlayerName
          // helper — falls back gracefully if either is missing.
          const fromOwnerId = data?.fromOwnerId as string | undefined;
          const toOwnerId = data?.toOwnerId as string | undefined;
          if (fromOwnerId && toOwnerId) {
            const attacker = this.lookupPlayerName(toOwnerId);
            const victim = this.lookupPlayerName(fromOwnerId);
            if (attacker && victim) {
              this.hud.showKillNotification(attacker, `${victim}'s drone`);
            }
          }
          break;
        }
        case ServerEvent.RematchVoteUpdate: {
          this.hud.showRematchModal({
            votes: (data?.votes as number) ?? 0,
            required: (data?.required as number) ?? REMATCH_MIN_VOTES,
            remainingMs: (data?.remainingMs as number) ?? 0,
          });
          break;
        }
        case ServerEvent.RematchReady: {
          this.hud.setRematchModalFinalMessage('Rematch ready! Joining...');
          void this.handleRematchReady();
          break;
        }
        case ServerEvent.RematchFailed: {
          this.hud.setRematchModalFinalMessage('Rematch failed — not enough votes.');
          // Repurpose the button as the menu exit so the user has a clear out.
          this.hud.setRematchButtonDisabled(false);
          this.hud.setRematchButtonMenuMode(() => this.handleReturnToMenu());
          break;
        }
        case ServerEvent.CardEffectFailed: {
          routeCardEffectFailed(
            data,
            this.net.getSessionId(),
            (msg) => this.hud.showCardFailedToast(msg),
          );
          break;
        }
      }
    });
  }

  /** Connect the post-match overlay's "REMATCH" button to the server. */
  private wireRematchOverlay(): void {
    this.hud.onRematchClick(() => {
      // Optimistic open so the user sees feedback before the server's first
      // RematchVoteUpdate. The server overwrites these values within a tick.
      this.hud.showRematchModal({
        votes: 0,
        required: REMATCH_MIN_VOTES,
        remainingMs: REMATCH_VOTE_WINDOW_SEC * 1000,
      });
      this.hud.setRematchButtonDisabled(true);
      this.net.getRoom()?.send('voteRematch');
    });
  }

  /**
   * Reload the page so the next colyseus `joinOrCreate` is routed by the
   * matchmaker to the freshly-created room. Full reload sidesteps a long
   * tail of "old InstancedMesh still mounted" issues from in-place rejoin.
   */
  private async handleRematchReady(): Promise<void> {
    try {
      this.hud.hideMatchEndOverlay();
      this.net.disconnect();
      window.location.reload();
    } catch (err) {
      console.error('Failed to follow rematch redirect:', err);
      this.hud.setRematchModalFinalMessage('Rematch redirect failed.');
    }
  }

  private handleReturnToMenu(): void {
    try {
      this.net.disconnect();
    } catch (err) {
      console.error('Failed to disconnect from room:', err);
    }
    window.location.href = window.location.pathname;
  }

  private getLocalGateState(): LocalGateState {
    const state = this.net.getRoom()?.state as any;
    const localSessionId = state ? this.resolveLocalSessionId(state) : this.net.getSessionId();
    const localDroneIds = new Set<string>();
    const localPlayer = state?.players?.get?.(localSessionId);
    localPlayer?.drones?.forEach?.((d: any) => {
      if (typeof d?.id === 'string') localDroneIds.add(d.id);
    });
    return { localSessionId, localDroneIds };
  }

  /**
   * Resolve a drone id to its `DroneType` enum index by scanning every
   * player's drones in the current room state. Returns `undefined` when
   * the id isn't found — DamageFloaters interprets that as environmental
   * damage and skips the icon + tint. Linear scan; called once per damage
   * event (not per frame) and N is bounded by the per-player drone cap.
   */
  private lookupDroneTypeById(droneId: string): number | undefined {
    if (!droneId) return undefined;
    const players = (this.net.getRoom()?.state as any)?.players;
    if (!players || typeof players.forEach !== 'function') return undefined;
    let droneType: number | undefined;
    players.forEach((p: any) => {
      if (droneType !== undefined) return;
      const drones = p?.drones;
      if (!drones || typeof drones.forEach !== 'function') return;
      drones.forEach((d: any) => {
        if (droneType !== undefined) return;
        if (typeof d?.id === 'string' && d.id === droneId && typeof d?.droneType === 'number') {
          droneType = d.droneType;
        }
      });
    });
    return droneType;
  }

  /**
   * Compute the live centroid of `playerId`'s alive drones from the latest
   * networked state. Returned each tick by the resolver passed into
   * `spawnCardSignature` so card-signature VFX follow a moving caster.
   * Returns null when the player has no alive drones; the caller leaves
   * the FX anchored to its last known position.
   */
  private centroidScratch: Vec3 = { x: 0, y: 0, z: 0 };

  private computeLivePlayerCentroid(playerId: string): Vec3 | null {
    const players = (this.net.getRoom()?.state as any)?.players;
    if (!players || typeof players.get !== 'function') return null;
    const player = players.get(playerId);
    const drones = player?.drones;
    if (!drones || typeof drones.forEach !== 'function') return null;
    let cx = 0, cy = 0, cz = 0, count = 0;
    drones.forEach((d: any) => {
      if (!d?.alive || !d.position) return;
      cx += d.position.x;
      cy += d.position.y;
      cz += d.position.z;
      count++;
    });
    if (count === 0) return null;
    // Reuse a single scratch object — card-signature resolvers fire at
    // 60-144Hz and only read x/y/z (Three.Vector3.set copies the values).
    this.centroidScratch.x = cx / count;
    this.centroidScratch.y = cy / count;
    this.centroidScratch.z = cz / count;
    return this.centroidScratch;
  }

  /**
   * Dispatch a damage-impact sound for the given attacker. Looks up the
   * attacker's `DroneType` via the same scan {@link lookupDroneTypeById}
   * uses, then routes to the matching per-type method on AudioManager.
   * Falls back to the generic `playDamageHit()` when the attacker isn't
   * found (defensive — environmental damage, dead-but-not-removed actors,
   * stale event after state churn, etc.).
   */
  private dispatchImpactAudio(attackerDroneId: string | undefined): void {
    if (!this.audio) return;
    if (!attackerDroneId) {
      this.audio.playDamageHit();
      return;
    }
    // Prefer the DroneStateManager per-frame index (O(1)). Fall back to the
    // schema scan only if the id isn't in the index yet (pre-first-tick edge
    // case during connect; after that the index is always current).
    const droneType =
      this.droneState.getDroneTypeById(attackerDroneId) ??
      this.lookupDroneTypeById(attackerDroneId);
    if (droneType === undefined) {
      this.audio.playDamageHit();
      return;
    }
    this.audio.playImpactByDroneType(droneType, attackerDroneId);
  }

  /**
   * Count non-eliminated players (excluding the training dummy).
   * Used as the denominator in "RANK X/N" on the eliminated overlay.
   */
  private getLivingPlayerTotal(): number {
    const room = this.net.getRoom();
    const players = (room?.state as any)?.players;
    if (!players || typeof players.forEach !== 'function') return 0;
    let total = 0;
    players.forEach((p: any) => {
      if (p?.sessionId === TRAINING_DUMMY_ID) return;
      total++;
    });
    return total;
  }

  /**
   * Fire a brief client-side hit-stop when the local player just landed a
   * kill. Skipped when the visualConfig disables screen-shake/hit-stop, when
   * the killer drone belongs to the training dummy (admin-mode only), or
   * when the kill was a self-frag (we don't celebrate accidentally killing
   * our own drone).
   */
  private maybeTriggerKillHitstop(data: Record<string, unknown>): void {
    if (!this.qualityPreset.screenShake) return;
    const killerDroneId = data?.killerId as string | undefined;
    if (!killerDroneId) return;
    const killerOwner = this.lookupOwnerOfDrone(killerDroneId);
    if (!killerOwner) return;
    if (killerOwner === TRAINING_DUMMY_ID) return;
    const localId = this.getLocalGateState().localSessionId;
    if (killerOwner !== localId) return;
    // Self-frag guard: the destroyed drone also belongs to us. Skip the
    // celebratory hit-stop so a friendly-fire mishap doesn't feel rewarding.
    const victimDroneId = data?.droneId as string | undefined;
    if (victimDroneId && this.getLocalGateState().localDroneIds.has(victimDroneId)) return;
    this.hitstopController.triggerHitstop();
  }

  /**
   * Fire the heavy-hit screen shake when an incoming damage event lands on
   * one of our drones with damage >= KNOCKBACK_DAMAGE_THRESHOLD. Targeting
   * the local player as attacker (no victim of ours) is intentionally NOT
   * shaken — the shake reads as "you got slammed", not "you slammed someone".
   */
  private maybeTriggerHeavyHitShake(
    data: Record<string, unknown>,
    gateState: LocalGateState,
  ): void {
    if (!this.qualityPreset.screenShake) return;
    const targetId = data?.targetDroneId;
    const isLocalVictim =
      typeof targetId === 'string' && gateState.localDroneIds.has(targetId);
    if (!isLocalVictim) return;
    const damage = typeof data?.damage === 'number' ? data.damage : 0;
    triggerHeavyHitShake(this.scene, damage);
  }

  /**
   * If the destroyed drone belonged to the local player AND it was their
   * last living drone, trigger the kill-cam. Free-mode only — BR mode
   * relies on the PlayerEliminated event (which carries the placement).
   */
  private maybeTriggerKillCamOnDroneDeath(data: Record<string, unknown>): void {
    if (!this.spectateCamera) return;
    const state = this.net.getRoom()?.state as any;
    if (!state) return;
    if (state.mode === GameMode.Br) return;

    const localId = this.resolveLocalSessionId(state);
    const localPlayer = state.players?.get?.(localId);
    if (!localPlayer) return;

    let aliveOwn = 0;
    for (const d of localPlayer.drones ?? []) {
      if (d.alive) aliveOwn++;
    }
    if (aliveOwn > 0) return;

    const killerId = data?.killerId as string | undefined;
    const droneId = data?.droneId as string | undefined;
    if (!droneId) return;
    const ownsDrone = (localPlayer.drones ?? []).some((d: any) => d.id === droneId);
    if (!ownsDrone) return;

    const killerOwner = killerId ? this.lookupOwnerOfDrone(killerId) : null;
    if (!killerOwner || killerOwner === localId) return;
    this.spectateCamera.triggerKillCam(killerOwner, GameClient.KILL_CAM_DURATION_MS);
  }

  private handleKillCamEnd(): void {
    const state = this.net.getRoom()?.state as any;
    if (state?.mode === GameMode.Br) {
      this.beginSpectate();
    }
  }

  private beginSpectate(): void {
    if (!this.spectateCamera) return;
    const survivors = this.collectSurvivors();
    if (survivors.length === 0) return;
    this.spectateCamera.enterSpectate(survivors);
  }

  private handleSpectateKey(e: KeyboardEvent): void {
    if (!this.spectateCamera) return;
    const mode = this.spectateCamera.getMode();
    if (mode !== 'spectate') return;
    if (e.key === 'ArrowRight') {
      this.spectateCamera.cycleSpectate(1);
      e.preventDefault();
    } else if (e.key === 'ArrowLeft') {
      this.spectateCamera.cycleSpectate(-1);
      e.preventDefault();
    } else if (e.key === 'Escape') {
      this.spectateCamera.exitSpectate();
      e.preventDefault();
    }
  }

  /** Living, non-eliminated, non-dummy player IDs other than the local one. */
  private collectSurvivors(): PlayerId[] {
    const state = this.net.getRoom()?.state as any;
    if (!state) return [];
    const localId = this.resolveLocalSessionId(state);
    const survivors: PlayerId[] = [];
    state.players?.forEach?.((p: any) => {
      if (!p) return;
      if (p.sessionId === TRAINING_DUMMY_ID) return;
      if (p.sessionId === localId) return;
      if (p.eliminated) return;
      const hasAlive = (p.drones ?? []).some((d: any) => d.alive);
      if (hasAlive) survivors.push(p.sessionId);
    });
    return survivors;
  }

  private lookupPlayerLeadPosition(playerId: PlayerId): Vec3 | null {
    const player = (this.net.getRoom()?.state as any)?.players?.get?.(playerId);
    if (!player) return null;
    const leaderId = player.leaderDroneId as string | undefined;
    if (leaderId) {
      const lead = (player.drones ?? []).find((d: any) => d.id === leaderId && d.alive);
      if (lead) return { x: lead.position.x, y: lead.position.y, z: lead.position.z };
    }
    return this.lookupPlayerAnyPosition(playerId);
  }

  private lookupPlayerAnyPosition(playerId: PlayerId): Vec3 | null {
    const player = (this.net.getRoom()?.state as any)?.players?.get?.(playerId);
    if (!player) return null;
    for (const d of player.drones ?? []) {
      if (d.alive) return { x: d.position.x, y: d.position.y, z: d.position.z };
    }
    const first = player.drones?.[0];
    if (first) return { x: first.position.x, y: first.position.y, z: first.position.z };
    return null;
  }

  private lookupPlayerName(playerId: PlayerId): string | null {
    const player = (this.net.getRoom()?.state as any)?.players?.get?.(playerId);
    return (player?.username as string) ?? null;
  }

  private lookupOwnerOfDrone(droneId: string): string | null {
    const players = (this.net.getRoom()?.state as any)?.players;
    if (!players?.forEach) return null;
    let owner: string | null = null;
    players.forEach((p: any) => {
      if (owner) return;
      if ((p.drones ?? []).some((d: any) => d.id === droneId)) owner = p.sessionId;
    });
    return owner;
  }

  private static readonly DRONE_NAMES = ['Striker', 'Sniper', 'Healer', 'Interceptor', 'Support', 'Guardian'];
  private static readonly TIER_NAMES = ['Common', 'Rare', 'Legend'];
  private static readonly TIER_COLORS = ['#aaaaaa', '#4a9eff', '#ff9900'];
  private static readonly CARD_NAMES = [
    'Regroup', 'Scatter', 'Follow', 'Orbit', 'Defensive Wall', 'Radar Ring',
    'Focus Fire', 'Relay Net', 'Cage Surround', 'Sync Dive', 'Convert Shot', 'Choreo Ultimate',
    'Horizontal Orbit', 'Diagonal Orbit',
  ];

  private detectPickups(localPlayer: Record<string, any>): void {
    const droneCount = localPlayer.drones?.length ?? 0;
    const cardCount = localPlayer.cards?.length ?? 0;

    if (droneCount > this.prevDroneCount && this.prevDroneCount > 0) {
      const newest = localPlayer.drones[droneCount - 1];
      if (newest) {
        const tierName = GameClient.TIER_NAMES[newest.tier] ?? 'Common';
        const droneName = GameClient.DRONE_NAMES[newest.droneType] ?? 'Drone';
        const color = GameClient.TIER_COLORS[newest.tier] ?? '#aaaaaa';
        this.hud.showPickupNotification(`${tierName} ${droneName}`, color);
      }
    }

    if (cardCount > this.prevCardCount && this.prevCardCount > 0) {
      const newest = localPlayer.cards[cardCount - 1];
      if (newest) {
        const tierName = GameClient.TIER_NAMES[newest.tier] ?? 'Common';
        const cardName = GameClient.CARD_NAMES[newest.cardType] ?? 'Algorithm';
        const color = GameClient.TIER_COLORS[newest.tier] ?? '#aaaaaa';
        this.hud.showPickupNotification(`${tierName} ${cardName}`, color);
      }
    }

    this.prevDroneCount = droneCount;
    this.prevCardCount = cardCount;
  }

  private updatePickups(deltaMs: number): void {
    const room = this.net.getRoom();
    if (!room?.state) return;

    const roomState = room.state as Record<string, unknown>;
    const pickups = roomState.pickups as
      | Map<string, Record<string, unknown>>
      | undefined;
    if (!pickups) return;

    const pickupData: Array<{
      id: string;
      pickupType: number;
      subType: number;
      tier: number;
      position: Vec3;
      collected: boolean;
    }> = [];

    pickups.forEach((p) => {
      const pos = p.position as Vec3;
      pickupData.push({
        id: p.id as string,
        pickupType: p.pickupType as number,
        subType: p.subType as number,
        tier: p.tier as number,
        position: { x: pos.x, y: pos.y, z: pos.z },
        collected: p.collected as boolean,
      });
    });

    this.pickupRenderer!.update(pickupData, deltaMs);
  }

  private updateHud(drones: InterpolatedDrone[], localId: string): void {
    const room = this.net.getRoom();
    const roomState = room?.state as any;
    const localSessionId = roomState ? this.resolveLocalSessionId(roomState) : localId;
    const localPlayer = roomState?.players?.get?.(localSessionId);

    if (roomState) {
      this.playerTracker.syncFromState(roomState, localSessionId);
    }
    if (localPlayer) {
      this.cardManager.syncFromServer(localPlayer.cards);
      this.detectCardReadyAndChirp(localPlayer.cards);
    }

    this.hud.getLeaderboard().update(this.playerTracker.getLeaderboard());
    this.hud.getCardBar().update(this.cardManager.getCardSlots());

    const dronePanel = this.hud.getDroneStatus();
    if (localPlayer) {
      const ids = localPlayer.selectedDroneIds
        ? Array.from(localPlayer.selectedDroneIds as Iterable<string>)
        : (localPlayer.selectedDroneId ? [localPlayer.selectedDroneId] : []);
      const leaderId = localPlayer.leaderDroneId ?? '';
      dronePanel.setSelection(ids, leaderId);
      this.droneRenderer?.setSelection(ids, leaderId);
    }

    const ownDrones = this.droneState.getOwnDrones();
    document.body.dataset.ownDroneCount = String(ownDrones.length);
    document.body.dataset.ownDroneCardSlots = ownDrones.map((d) => String(d.activeCardSlot)).join(',');

    this.updateLowHpHeartbeat(ownDrones);

    dronePanel.update(
      ownDrones.map((d) => ({
        droneId: d.id,
        droneType: d.droneType,
        tier: d.tier,
        healthFraction: d.maxHealth > 0 ? d.health / d.maxHealth : 0,
        alive: d.alive,
        abilityCooldown: d.abilityCooldown,
        activeCardSlot: d.activeCardSlot,
        activeCardType: d.activeCardType,
      })),
    );

    const minimapEntities: Array<{ position: { x: number; y: number; z: number }; color: string; size: number }> = [];

    // Add drones
    for (const d of drones) {
      if (!d.alive) continue;
      minimapEntities.push({
        position: d.position,
        color: d.ownerId === localSessionId ? '#00c8ff' : '#ff4444',
        size: d.ownerId === localSessionId ? 3 : 2,
      });
    }

    // Add pickups
    if (roomState) {
      const pickups = roomState.pickups as Map<string, Record<string, unknown>> | undefined;
      if (pickups) {
        pickups.forEach((p) => {
          if (p.collected) return;
          const pos = p.position as { x: number; y: number; z: number };
          minimapEntities.push({
            position: { x: pos.x, y: pos.y, z: pos.z },
            color: '#ffaa00',
            size: 2,
          });
        });
      }
    }

    this.hud.getMinimap().update(
      minimapEntities,
      this.droneState.getSwarmCenter(),
      roomState,
      this.cameraController?.getOrbitAngle() ?? 0,
    );
    this.updateBrPanelVisibility(roomState);
  }

  private getFrameDrones(now: number): InterpolatedDrone[] {
    const interpolated = this.interpolator!.getInterpolatedDrones(now);
    if (interpolated.length > 0) return interpolated;

    const state = this.net.getRoom()?.state as any;
    if (!state) return interpolated;

    this.handleStateSnapshot(state);
    const replayed = this.interpolator!.getInterpolatedDrones(now);
    return replayed.length > 0 ? replayed : this.extractDronesFromState(state);
  }

  private extractDronesFromState(state: any): InterpolatedDrone[] {
    const drones: InterpolatedDrone[] = [];
    state.players?.forEach?.((player: any) => {
      player.drones?.forEach?.((drone: any) => {
        drones.push(rawDroneToInterpolated(drone));
      });
    });
    return drones;
  }

  /**
   * Backtick (`) toggles the diagnostic panel. Suppressed when typing in an
   * input/textarea/contentEditable so chat & username fields keep working.
   */
  private handleCombatLogKey(e: KeyboardEvent): void {
    if (e.key !== '`' && e.code !== 'Backquote') return;
    const target = e.target as HTMLElement | null;
    if (target) {
      const tag = target.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      if (target.isContentEditable) return;
    }
    e.preventDefault();
    this.combatLog?.toggle();
  }

  /**
   * Queue VHS post-FX on for `durationMs`, then off. If a previous pulse is
   * still pending its off-timer is cancelled so a back-to-back trigger does
   * not get cut short.
   */
  private pulseVHS(durationMs: number): void {
    if (this.vhsOffTimer !== null) {
      clearTimeout(this.vhsOffTimer);
      this.vhsOffTimer = null;
    }
    this.scene.setVHSEnabled(true);
    this.vhsOffTimer = setTimeout(() => {
      this.scene.setVHSEnabled(false);
      this.vhsOffTimer = null;
    }, durationMs);
  }

  /**
   * Trigger {@link pulseVHS} when a CardActivated event names the local
   * player AND the activated card is Legend tier. Other tiers and remote
   * players are intentional no-ops.
   */
  private maybeTriggerLegendVHS(data: Record<string, unknown>): void {
    if (data?.playerId !== this.net.getSessionId()) return;
    const cardType = data.cardType as AlgorithmCardType;
    const card = ALGORITHM_CARDS[cardType];
    if (!card || card.tier !== Tier.Legend) return;
    this.pulseVHS(GameClient.VHS_LEGENDARY_DURATION_MS);
  }

  /**
   * Activates the slow heartbeat SFX when the local swarm's aggregate HP
   * dips under 25%, deactivates once it recovers above 30%.
   * Hysteresis avoids flicker at the threshold.
   */
  private updateLowHpHeartbeat(
    ownDrones: ReadonlyArray<{ alive: boolean; health: number; maxHealth: number }>,
  ): void {
    if (!this.audio) return;
    let cur = 0;
    let max = 0;
    for (const d of ownDrones) {
      if (!d.alive) continue;
      cur += d.health;
      max += d.maxHealth;
    }
    if (max <= 0) {
      if (this.lowHpAudioActive) {
        this.audio.setLowHpHeartbeat(false);
        this.lowHpAudioActive = false;
      }
      return;
    }
    const fraction = cur / max;
    if (!this.lowHpAudioActive && fraction < 0.25) {
      this.audio.setLowHpHeartbeat(true, fraction);
      this.lowHpAudioActive = true;
    } else if (this.lowHpAudioActive && fraction > 0.30) {
      this.audio.setLowHpHeartbeat(false);
      this.lowHpAudioActive = false;
    }
  }

  private playMatchEndAudio(winnerUsername: string | undefined, localUsername: string): void {
    if (!this.audio) return;
    this.audio.stopBgmLoop();
    this.audio.setLowHpHeartbeat(false);
    if (winnerUsername && winnerUsername === localUsername) {
      this.audio.playVictoryFanfare();
    } else {
      this.audio.playDefeatSting();
    }
  }

  /**
   * Fire a single ready-chirp on each per-slot cooldownRemaining rising-edge
   * (prev > 0, now === 0). Newly-observed slots start with `null` so the
   * initial spawn (cards arriving with cooldown 0) cannot chirp.
   *
   * Returns the chirp count for tests.
   */
  private detectCardReadyAndChirp(cards: unknown): number {
    if (!cards) {
      this.prevCardCooldowns = [];
      return 0;
    }
    const cardArray = Array.isArray(cards)
      ? cards
      : Array.from(cards as Iterable<{ cooldownRemaining: number }>);

    let chirps = 0;
    const next: Array<number | null> = new Array(cardArray.length);
    for (let i = 0; i < cardArray.length; i++) {
      const card = cardArray[i] as { cooldownRemaining?: number };
      const cur = typeof card?.cooldownRemaining === 'number' ? card.cooldownRemaining : 0;
      const prev = this.prevCardCooldowns[i];
      if (prev != null && prev > 0 && cur === 0) {
        this.audio?.playCardReadyChirp();
        chirps++;
      }
      next[i] = cur;
    }
    this.prevCardCooldowns = next;
    return chirps;
  }

  private updateBrPanelVisibility(roomState: any): void {
    const shouldShow = roomState?.mode === GameMode.Br && roomState?.phase === GamePhase.Playing;
    if (shouldShow && !this.brPanelVisible) {
      this.hud.getBrStatusPanel().show();
      this.brPanelVisible = true;
    } else if (!shouldShow && this.brPanelVisible) {
      this.hud.getBrStatusPanel().hide();
      this.brPanelVisible = false;
    }
  }
}
